import { AppError } from './app-error.model';
import { AUTH_ERROR_MESSAGES, DEFAULT_ERROR_MESSAGES, RESET_CREDENTIAL_POLICY } from './error-message.constants';

export function mapOktaError(error: unknown, context?: 'login' | 'otp' | 'recovery' | 'reset'): AppError {
    const text = getErrorText(error).toLowerCase();

    if (includesAny(text, ['locked', 'suspend', 'bloque'])) {
        return authError(AUTH_ERROR_MESSAGES.lockedAccount);
    }

    if (context === 'reset') {
        if (isResetPasswordPolicyError(text)) {
            return {
                type: 'validation',
                title: AUTH_ERROR_MESSAGES.resetRequirementsTitle,
                message: AUTH_ERROR_MESSAGES.resetRequirementsMessage,
                fieldErrors: RESET_CREDENTIAL_POLICY.map((message) => ({ field: 'newPassword', message })),
                retryable: false
            };
        }

        return {
            type: 'service',
            title: DEFAULT_ERROR_MESSAGES.service.title,
            message: AUTH_ERROR_MESSAGES.resetFailed,
            retryable: true
        };
    }

    if (includesAny(text, ['unauthorized', 'not authorized', 'forbidden', 'access_denied', 'access denied', '401', '403'])) {
        return {
            type: 'authorization',
            title: DEFAULT_ERROR_MESSAGES.authorization.title,
            message: AUTH_ERROR_MESSAGES.unauthorizedLogin,
            retryable: false
        };
    }

    if (includesAny(text, ['expired', 'caduc', 'timeout'])) {
        if (context === 'otp') {
            return authError(AUTH_ERROR_MESSAGES.expiredOtp);
        }
        return authError(AUTH_ERROR_MESSAGES.expiredSession);
    }

    if (context === 'otp' || (context !== 'recovery' && includesAny(text, ['otp', 'verification', 'codigo', 'code']))) {
        return authError(AUTH_ERROR_MESSAGES.invalidOtp);
    }

    if (context === 'recovery') {
        return {
            type: 'authentication',
            title: DEFAULT_ERROR_MESSAGES.authentication.title,
            message: AUTH_ERROR_MESSAGES.recoveryFailed,
            retryable: false
        };
    }

    return authError(AUTH_ERROR_MESSAGES.invalidCredentials);
}

function authError(message: string): AppError {
    return {
        type: 'authentication',
        title: DEFAULT_ERROR_MESSAGES.authentication.title,
        message,
        retryable: false
    };
}

function getErrorText(error: unknown): string {
    if (!error) {
        return '';
    }

    if (typeof error === 'string') {
        return error;
    }

    if (typeof error !== 'object') {
        return '';
    }

    return flattenErrorText(error)
        .filter((item): item is string => typeof item === 'string')
        .join(' ');
}

function includesAny(value: string, needles: string[]): boolean {
    return needles.some((needle) => value.includes(needle));
}

function isResetPasswordPolicyError(text: string): boolean {
    const mentionsPassword = includesAny(text, [
        'password',
        'contraseña',
        'contrasena',
        'credential'
    ]);

    return mentionsPassword && includesAny(text, [
        'requirement',
        'policy',
        'requirements',
        'complexity',
        'history',
        'username',
        'first name',
        'last name',
        'minimum age',
        'last 8',
        'current',
        'actual',
        'password.current',
        'passcode',
        'reset-authenticator'
    ]);
}

function flattenErrorText(error: unknown): unknown[] {
    if (!error || typeof error !== 'object') {
        return [error];
    }

    const candidate = error as Record<string, unknown>;
    const nested = [
        candidate.errorSummary,
        candidate.message,
        candidate.errorCode,
        candidate.name,
        candidate.status,
        candidate.statusCode,
        candidate.error,
        candidate.error_description,
        ...flattenMessages(candidate.messages),
        ...flattenMessages(candidate.errorCauses),
        ...flattenMessages((candidate.xhr as Record<string, unknown> | undefined)?.status),
        ...flattenMessages((candidate.xhr as Record<string, unknown> | undefined)?.statusCode),
        ...flattenMessages((candidate.xhr as Record<string, unknown> | undefined)?.responseJSON),
        ...flattenMessages((candidate.xhr as Record<string, unknown> | undefined)?.responseText),
        ...flattenMessages(candidate.rawIdxState),
        ...flattenMessages(candidate.nextStep),
        ...flattenMessages(candidate.remediation),
        ...flattenMessages(candidate.currentAuthenticator),
        ...flattenMessages(candidate.recoveryAuthenticator),
        ...flattenMessages(candidate.user)
    ];

    return nested;
}

function flattenMessages(value: unknown): unknown[] {
    if (!value) {
        return [];
    }

    if (typeof value === 'string' || typeof value === 'number') {
        return [String(value)];
    }

    if (Array.isArray(value)) {
        return value.flatMap((item) => flattenMessages(item));
    }

    if (typeof value !== 'object') {
        return [];
    }

    const candidate = value as Record<string, unknown>;
    return [
        candidate.message,
        candidate.i18n,
        candidate.class,
        candidate.key,
        candidate.name,
        candidate.label,
        candidate.errorSummary,
        candidate.errorCode,
        ...flattenMessages(candidate.value),
        ...flattenMessages(candidate.messages),
        ...flattenMessages(candidate.form),
        ...flattenMessages(candidate.remediation),
        ...flattenMessages(candidate.currentAuthenticator),
        ...flattenMessages(candidate.recoveryAuthenticator),
        ...flattenMessages(candidate.user)
    ];
}
